import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Mappage des langues pour les balises meta
const LANGUAGE_MAP = {
  en: { locale: 'en_US', hreflang: 'en' },
  fr: { locale: 'fr_FR', hreflang: 'fr' },
  es: { locale: 'es_ES', hreflang: 'es' },
  ru: { locale: 'ru_RU', hreflang: 'ru' },
};

// SEO par défaut selon les langues
const DEFAULT_SEO = {
  en: {
    title: 'Israel Population & Immigration Authority - ETA-IL and Official Entry Procedures',
    description: 'Official website of the Israel Population & Immigration Authority. Official information on ETA-IL, eVisa-B2 and entry procedures to Israel. Electronic travel authorization application.',
    keywords: 'Israel Population Immigration Authority, ETA-IL, eVisa-B2, electronic travel authorization, Israel entry procedures, Israel visa, PIBA',
    ogTitle: 'Israel Population & Immigration Authority - ETA-IL and Official Entry Procedures',
    ogDescription: 'Official website of the Israel Population & Immigration Authority. Official information on ETA-IL, eVisa-B2 and entry procedures to Israel.',
  },
  fr: {
    title: 'Autorité de la population et de l\'immigration d\'Israël - ETA-IL et procédures d\'entrée officielles',
    description: 'Site officiel de l\'Autorité de la population et de l\'immigration d\'Israël. Informations officielles sur l\'ETA-IL, eVisa-B2 et les procédures d\'entrée en Israël. Demande d\'autorisation de voyage électronique.',
    keywords: 'Autorité population immigration Israël, ETA-IL, eVisa-B2, autorisation voyage électronique, procédures entrée Israël, visa Israël, PIBA',
    ogTitle: 'Autorité de la population et de l\'immigration d\'Israël - ETA-IL et procédures d\'entrée officielles',
    ogDescription: 'Site officiel de l\'Autorité de la population et de l\'immigration d\'Israël. Informations officielles sur l\'ETA-IL, eVisa-B2 et les procédures d\'entrée en Israël.',
  },
  es: {
    title: 'Autoridad de Población e Inmigración de Israel - ETA-IL y Procedimientos Oficiales de Entrada',
    description: 'Sitio web oficial de la Autoridad de Población e Inmigración de Israel. Información oficial sobre ETA-IL, eVisa-B2 y procedimientos de entrada a Israel. Solicitud de autorización de viaje electrónica.',
    keywords: 'Autoridad Población Inmigración Israel, ETA-IL, eVisa-B2, autorización viaje electrónico, procedimientos entrada Israel, visa Israel, PIBA',
    ogTitle: 'Autoridad de Población e Inmigración de Israel - ETA-IL y Procedimientos Oficiales de Entrada',
    ogDescription: 'Sitio web oficial de la Autoridad de Población e Inmigración de Israel. Información oficial sobre ETA-IL, eVisa-B2 y procedimientos de entrada a Israel.',
  },
  ru: {
    title: 'Управление по делам населения и иммиграции Израиля - ETA-IL и официальные процедуры въезда',
    description: 'Официальный веб-сайт Управления по делам населения и иммиграции Израиля. Официальная информация о ETA-IL, eVisa-B2 и процедурах въезда в Израиль. Заявка на электронное разрешение на поездку.',
    keywords: 'Управление населения иммиграции Израиль, ETA-IL, eVisa-B2, электронное разрешение поездка, процедуры въезда Израиль, виза Израиль, PIBA',
    ogTitle: 'Управление по делам населения и иммиграции Израиля - ETA-IL и официальные процедуры въезда',
    ogDescription: 'Официальный веб-сайт Управления по делам населения и иммиграции Израиля. Официальная информация о ETA-IL, eVisa-B2 и процедурах въезда в Израиль.',
  },
};

const useSEO = ({
  titleKey,
  descriptionKey,
  customTitle,
  customDescription,
  customKeywords,
  customOgTitle,
  customOgDescription,
  noIndex = false,
} = {}) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'en';
  const langConfig = LANGUAGE_MAP[currentLang] || LANGUAGE_MAP.en;
  const defaultSeo = DEFAULT_SEO[currentLang] || DEFAULT_SEO.en;

  useEffect(() => {
    // Titre de la page
    const title = customTitle || 
                 (titleKey ? t(titleKey) : null) || 
                 defaultSeo.title;
    
    if (title) {
      document.title = title;
    }

    // Meta description
    const description = customDescription || 
                       (descriptionKey ? t(descriptionKey) : null) || 
                       defaultSeo.description;
    
    updateMetaTag('description', description);

    // Meta keywords
    const keywords = customKeywords || defaultSeo.keywords;
    updateMetaTag('keywords', keywords);

    // Meta robots
    updateMetaTag('robots', noIndex ? 'noindex, nofollow' : 'index, follow');

    // Open Graph
    const ogTitle = customOgTitle || title;
    const ogDescription = customOgDescription || description;
    
    updateMetaProperty('og:title', ogTitle);
    updateMetaProperty('og:description', ogDescription);
    updateMetaProperty('og:locale', langConfig.locale);
    updateMetaProperty('og:url', window.location.href);

    // Twitter
    updateMetaProperty('twitter:title', ogTitle);
    updateMetaProperty('twitter:description', ogDescription);

    // Lang attribute
    document.documentElement.lang = langConfig.hreflang;

    // Canonical URL (sans paramètres de langue)
    updateCanonicalUrl();

  }, [
    titleKey, 
    descriptionKey, 
    customTitle, 
    customDescription, 
    customKeywords,
    customOgTitle,
    customOgDescription,
    noIndex,
    currentLang,
    t
  ]);
};

// Fonctions utilitaires
const updateMetaTag = (name, content) => {
  if (!content) return;
  
  let meta = document.querySelector(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = name;
    document.head.appendChild(meta);
  }
  meta.content = content;
};

const updateMetaProperty = (property, content) => {
  if (!content) return;
  
  let meta = document.querySelector(`meta[property="${property}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('property', property);
    document.head.appendChild(meta);
  }
  meta.content = content;
};

const updateCanonicalUrl = () => {
  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.appendChild(canonical);
  }
  
  // URL canonique sans paramètres de langue
  const baseUrl = window.location.origin + window.location.pathname;
  canonical.href = baseUrl;
};

export default useSEO; 